/**
 * Expanded matching row: profile, selected slot, and compatible candidates.
 */

import * as React from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/forms/Button';
import { SearchInput } from '@/components/forms/SearchInput';
import { Spinner } from '@/components/ui/spinner';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { LabelBadge } from '@/components/common/LabelBadge';
import {
  Plus,
  X,
  Eye,
  CheckCircle,
  Clock,
  User,
  Users,
  GraduationCap,
  ScanSearch,
  HeartHandshake,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { matchingDashboardKeys, matchingStatsKey } from '@/hooks/useMatchingDashboard';
import { CreateSessionView } from '@/components/sessions/remote/CreateSessionView';
import { MatchingProfileModal } from './MatchingProfileModal';
import { useAuthStore } from '@/stores/authStore';
import { getRoleLabel, getRoleLabelPlural } from '@/utils/role-label-utils';
import {
  MATCHING_STATUS_CONFIG,
  getProgramBadgeColor,
  LEVEL_BADGE_CONFIG,
  getLevelBadgeColor,
  PROGRAM_DISPLAY_CONFIG,
  SUBJECT_DISPLAY_CONFIG,
  getSubjectBadgeColor,
  MATCHING_CANDIDATES_PAGE_SIZE,
} from '@/components/pairs/pair-configs';
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  useSensors,
  useSensor,
  PointerSensor,
  pointerWithin,
  closestCenter,
} from '@dnd-kit/core';
import type { DragStartEvent, DragEndEvent, CollisionDetection } from '@dnd-kit/core';
import type {
  TutorDashboardItem,
  StudentDashboardItem,
  AvailabilitySlot,
  UnmatchedStudentItem,
  UnmatchedTutorItem,
} from '@/types/remote/matching.types';
import type { Program } from '@/types/program.types';
import {
  buildCandidatesQueryParams,
  buildCheckAvailabilityRequest,
  buildMatchedQueryParams,
  buildMatchingSessionCreatePrefill,
  buildSlotVerifyMap,
  filterVisibleCandidates,
  findCandidateById,
  findFirstFreeSlot,
  getCandidateCardOpacity,
  getCandidateDisplayName,
  getCandidateId,
  getCommonLevelsForStudent,
  getCommonProgramsForStudent,
  getDashboardNamedLevels,
  getDashboardRawSubject,
  getTutorDashboardValorisation,
  getTutorValorisationBadge,
  getDropZoneEmptyMessage,
  canAddCandidate,
  getAssignabilityConfig,
  getEmptyCandidatesMessage,
  getMatchingCapacityLabel,
  getMatchingGenderConfig,
  getRemoteSubjectLabel,
  getSlotDisabledTooltip,
  getSlotPillClassName,
  formatSlotLabel,
  getProgramDisplayName,
  getSlotPillStyle,
  getSlotVisualState,
  getTutorCapacityColor,
  getTutorRemainingCapacity,
  isConfirmPairDisabled,
  resolveAddedCandidateProgramId,
  resolveMatchingAssignability,
  sortAvailabilitySlots,
  matchingCandidatesKeys,
  useCheckAvailability,
  useStudentTutorMatched,
  useStudentTutorsUnmatched,
  useTutorStudentsMatched,
  useTutorStudentsUnmatched,
  type MatchingCandidate,
  type MatchingDashboardItem,
  type MatchingPerspective,
  type SlotVerifyResult,
} from '@/components/pairs/remote/matching-utils';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';

/** Props for {@link MatchingCollapse}. */
interface MatchingCollapseProps {
  readonly data: TutorDashboardItem | StudentDashboardItem;
  readonly perspective: 'tutor' | 'student';
  readonly programsList: Program[];
}

/** Expanded matching row: profile, selected slot, and compatible candidates. */
export function MatchingCollapse({ data, perspective, programsList }: MatchingCollapseProps) {
  const [selectedSlot, setSelectedSlot] = React.useState<AvailabilitySlot | null>(null);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [candidatePage, setCandidatePage] = React.useState(1);
  const [isVerifying, setIsVerifying] = React.useState(true);
  const [verifyClicked, setVerifyClicked] = React.useState(false);
  const [addedCandidates, setAddedCandidates] = React.useState<string[]>([]);
  const [addedCandidateProgramId, setAddedCandidateProgramId] = React.useState<string | undefined>(
    undefined,
  );
  const [profileModalUserId, setProfileModalUserId] = React.useState<string | null>(null);
  const [profileModalPerspective, setProfileModalPerspective] =
    React.useState<MatchingPerspective>(perspective);
  const [showCreateSession, setShowCreateSession] = React.useState(false);
  const [slotVerifyMap, setSlotVerifyMap] = React.useState<Map<string, SlotVerifyResult>>(
    new Map(),
  );
  const [activeDragCandidateId, setActiveDragCandidateId] = React.useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  /** Prefer the droppable under the pointer so closest-center does not steal the drop zone. */
  const collisionDetection: CollisionDetection = React.useCallback((args) => {
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) return pointerCollisions;
    return closestCenter(args);
  }, []);

  const queryClient = useQueryClient();
  const companyId = useAuthStore((state) => state.user?.companyId);
  const checkAvailabilityMutation = useCheckAvailability();

  const matchedParams = buildMatchedQueryParams(data, perspective, selectedSlot);
  useTutorStudentsMatched(perspective === 'tutor' ? matchedParams : null);
  useStudentTutorMatched(perspective === 'student' ? matchedParams : null);

  /** New search or slot starts candidates at page 1. */
  React.useEffect(() => {
    setCandidatePage(1);
  }, [searchQuery, selectedSlot]);

  const candidatesParams = buildCandidatesQueryParams(
    data,
    perspective,
    selectedSlot,
    candidatePage,
    MATCHING_CANDIDATES_PAGE_SIZE,
    searchQuery,
  );
  const tutorCandidatesQuery = useTutorStudentsUnmatched(
    perspective === 'tutor' ? candidatesParams : null,
  );
  const studentCandidatesQuery = useStudentTutorsUnmatched(
    perspective === 'student' ? candidatesParams : null,
  );
  const candidatesData =
    perspective === 'tutor'
      ? (tutorCandidatesQuery.data?.data ?? [])
      : (studentCandidatesQuery.data?.data ?? []);
  const candidatesTotal =
    perspective === 'tutor'
      ? (tutorCandidatesQuery.data?.total ?? 0)
      : (studentCandidatesQuery.data?.total ?? 0);
  const totalCandidatePages = Math.max(
    1,
    Math.ceil(candidatesTotal / MATCHING_CANDIDATES_PAGE_SIZE),
  );
  const isCandidatesLoading = tutorCandidatesQuery.isFetching || studentCandidatesQuery.isFetching;

  const matchingStatusConfig = MATCHING_STATUS_CONFIG[data.matchingStatus];
  const genderConfig = getMatchingGenderConfig(data.gender, perspective);
  const assignabilityConfig = React.useMemo(
    () => resolveMatchingAssignability(verifyClicked, slotVerifyMap, data.affectability),
    [verifyClicked, slotVerifyMap, data.affectability],
  );

  const verifyInFlightRef = React.useRef(false);
  const didAutoVerifyRef = React.useRef(false);
  const userId = data.userId;
  const programId = data.programs[0]?.programId || '';
  const availability = data.availability;
  const isTutor = perspective === 'tutor';

  /** Verify all slots (also on banner open), then auto-select the first free assignable one. */
  const handleVerify = React.useCallback(async () => {
    if (verifyInFlightRef.current) return;
    verifyInFlightRef.current = true;
    setIsVerifying(true);
    try {
      const result = await checkAvailabilityMutation.mutateAsync(
        buildCheckAvailabilityRequest({
          isTutor,
          userId,
          programId,
          availability,
        }),
      );
      const verifyMap = buildSlotVerifyMap(result.data);
      setSlotVerifyMap(verifyMap);
      const firstFreeSlot = findFirstFreeSlot(availability, verifyMap);
      if (firstFreeSlot) setSelectedSlot(firstFreeSlot);
      setVerifyClicked(true);
      toast.success('Vérification terminée');
    } catch (error: unknown) {
      toast.error(getErrorMessage(error));
    } finally {
      verifyInFlightRef.current = false;
      setIsVerifying(false);
    }
  }, [availability, checkAvailabilityMutation, isTutor, programId, userId]);

  React.useEffect(() => {
    if (didAutoVerifyRef.current) return;
    didAutoVerifyRef.current = true;
    void handleVerify();
  }, [handleVerify]);

  /** Drop zone holds a single candidate; ignore further adds. */
  const handleAddCandidate = (candidateId: string) => {
    if (addedCandidates.length > 0) return;
    setAddedCandidates([candidateId]);
    setAddedCandidateProgramId(
      resolveAddedCandidateProgramId(perspective, candidateId, candidatesData),
    );
  };

  /** Remove a candidate from the drop zone. */
  const handleRemoveCandidate = (candidateId: string) => {
    setAddedCandidates((prev) => prev.filter((id) => id !== candidateId));
  };

  /** Track the dragged candidate so the overlay can render it. */
  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragCandidateId(event.active.id as string);
  };

  /** Accept drops only on `candidate-drop-zone` and only after a slot is selected. */
  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragCandidateId(null);
    if (event.over?.id !== 'candidate-drop-zone') return;
    if (!selectedSlot) {
      toast.error(getErrorMessage('selectAvailabilitySlot'));
      return;
    }
    handleAddCandidate(event.active.id as string);
  };

  /** Close create-session and clear the drop zone. */
  const handleCancelCreateSession = () => {
    setShowCreateSession(false);
    setAddedCandidates([]);
    setAddedCandidateProgramId(undefined);
  };

  /** Session-create API error toast. */
  const handleSessionCreationError = (err: unknown) => {
    toast.error(getErrorMessage(err));
  };

  /** Close the dialog and refresh matching stats / dashboard / candidates. */
  const handleSessionCreationSuccess = async () => {
    setShowCreateSession(false);
    setAddedCandidates([]);
    setAddedCandidateProgramId(undefined);
    toast.success('Affectation du binôme effectuée et séance planifiée avec succès.');
    if (!companyId) return;
    await queryClient.invalidateQueries({ queryKey: matchingStatsKey(companyId) });
    await queryClient.invalidateQueries({ queryKey: matchingDashboardKeys.all });
    await queryClient.invalidateQueries({ queryKey: matchingCandidatesKeys.all });
  };

  const addedCandidate = findCandidateById(candidatesData, addedCandidates[0] ?? '');
  const addedCandidateName = addedCandidate ? getCandidateDisplayName(addedCandidate) : undefined;
  const sortedAvailability = sortAvailabilitySlots(data.availability);
  const isConfirmDisabled = isConfirmPairDisabled(
    selectedSlot,
    addedCandidates.length,
    perspective,
    data,
  );
  const visibleCandidates = filterVisibleCandidates(candidatesData, addedCandidates);

  /** Subject / level / program badges; tutor cards show only levels/programs shared with the student. */
  const renderCandidateBadges = (candidate: MatchingCandidate) => (
    <MatchingCandidateBadges
      candidate={candidate}
      dashboardData={data}
      programsList={programsList}
    />
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div
        className="grid grid-cols-3 gap-4 p-4"
        style={{
          backgroundColor: 'color-mix(in srgb, var(--color-1-nuance-3) 5%, transparent)',
        }}
      >
        <MatchingExpandedLeftColumn
          data={data}
          perspective={perspective}
          matchingStatusConfig={matchingStatusConfig}
          genderConfig={genderConfig}
          assignabilityConfig={assignabilityConfig}
          verifyClicked={verifyClicked}
          isVerifying={isVerifying}
          programsList={programsList}
          sortedAvailability={sortedAvailability}
          selectedSlot={selectedSlot}
          slotVerifyMap={slotVerifyMap}
          onOpenProfile={() => {
            setProfileModalPerspective(perspective);
            setProfileModalUserId(data.userId);
          }}
          onVerify={handleVerify}
          onSelectSlot={setSelectedSlot}
        />

        <DroppableCenterColumn
          selectedSlot={selectedSlot}
          perspective={perspective}
          addedCandidates={addedCandidates}
          candidatesData={candidatesData}
          isConfirmDisabled={isConfirmDisabled}
          onRemoveCandidate={handleRemoveCandidate}
          onConfirm={() => setShowCreateSession(true)}
          renderCandidateBadges={renderCandidateBadges}
        />

        <MatchingExpandedRightColumn
          perspective={perspective}
          searchQuery={searchQuery}
          onSearch={setSearchQuery}
          candidatesTotal={candidatesTotal}
          candidatePage={candidatePage}
          totalCandidatePages={totalCandidatePages}
          onPrevPage={() => setCandidatePage((page) => Math.max(1, page - 1))}
          onNextPage={() => setCandidatePage((page) => Math.min(totalCandidatePages, page + 1))}
          isLoading={isCandidatesLoading}
          visibleCandidates={visibleCandidates}
          selectedSlot={selectedSlot}
          addedCount={addedCandidates.length}
          onEyeClick={(candidateId) => {
            setProfileModalPerspective(perspective === 'tutor' ? 'student' : 'tutor');
            setProfileModalUserId(candidateId);
          }}
          onPlusClick={handleAddCandidate}
          renderCandidateBadges={renderCandidateBadges}
        />
      </div>

      <MatchingProfileModal
        userId={profileModalUserId}
        perspective={profileModalPerspective}
        onClose={() => setProfileModalUserId(null)}
      />

      <Dialog
        open={showCreateSession}
        onOpenChange={(open) => {
          if (!open) handleCancelCreateSession();
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 custom-scroll-area" hideCloseButton>
          {showCreateSession && (
            <CreateSessionView
              sessionType="tutoring"
              onClose={handleCancelCreateSession}
              onSuccess={handleSessionCreationSuccess}
              onError={handleSessionCreationError}
              {...buildMatchingSessionCreatePrefill({
                perspective,
                data,
                addedCandidateId: addedCandidates[0],
                addedCandidateName,
                addedCandidateProgramId,
                selectedSlot,
              })}
            />
          )}
        </DialogContent>
      </Dialog>

      {typeof document !== 'undefined' &&
        createPortal(
          <DragOverlay zIndex={9999}>
            {activeDragCandidateId && (
              <CandidateCardOverlay
                candidate={findCandidateById(candidatesData, activeDragCandidateId)}
                renderBadges={renderCandidateBadges}
              />
            )}
          </DragOverlay>,
          document.body,
        )}
    </DndContext>
  );
}

/** Student: own badges. Tutor: only levels/programs shared with the dashboard student. */
function MatchingCandidateBadges({
  candidate,
  dashboardData,
  programsList,
}: {
  readonly candidate: MatchingCandidate;
  readonly dashboardData: MatchingDashboardItem;
  readonly programsList: Program[];
}) {
  if ('studentUserId' in candidate) {
    return (
      <div className="flex flex-wrap gap-1 mt-1">
        {candidate.subject && (
          <LabelBadge
            label={getRemoteSubjectLabel(candidate.subject)}
            icon={SUBJECT_DISPLAY_CONFIG.icon}
            variant="outlined"
            color={getSubjectBadgeColor(candidate.subject)}
            size="sm"
          />
        )}
        {candidate.level?.map((level) => (
          <LabelBadge
            key={level.levelId}
            label={level.levelName ?? '…'}
            icon={LEVEL_BADGE_CONFIG.icon}
            variant={LEVEL_BADGE_CONFIG.variant}
            color={getLevelBadgeColor(level.levelName)}
            size="sm"
          />
        ))}
        {candidate.programs.slice(0, 2).map((program) => {
          const name = getProgramDisplayName(program, programsList);
          return (
            <LabelBadge
              key={program.programId}
              label={name}
              icon={PROGRAM_DISPLAY_CONFIG.icon}
              variant="outlined"
              color={getProgramBadgeColor(name)}
              size="sm"
            />
          );
        })}
      </div>
    );
  }

  const studentData = dashboardData as StudentDashboardItem;
  const commonLevels = getCommonLevelsForStudent(candidate.levels, studentData);
  const commonPrograms = getCommonProgramsForStudent(candidate.programs, studentData);

  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {candidate.subjectToTeach && (
        <LabelBadge
          label={getRemoteSubjectLabel(candidate.subjectToTeach)}
          icon={SUBJECT_DISPLAY_CONFIG.icon}
          variant="outlined"
          color={getSubjectBadgeColor(candidate.subjectToTeach)}
          size="sm"
        />
      )}
      {commonLevels.map((level) => {
        const label = level.levelName ?? level.levelId;
        return (
          <LabelBadge
            key={level.levelId}
            label={label}
            icon={LEVEL_BADGE_CONFIG.icon}
            variant={LEVEL_BADGE_CONFIG.variant}
            color={getLevelBadgeColor(label)}
            size="sm"
          />
        );
      })}
      {commonPrograms.map((program) => {
        const name = getProgramDisplayName(program, programsList);
        return (
          <LabelBadge
            key={program.programId}
            label={name}
            icon={PROGRAM_DISPLAY_CONFIG.icon}
            variant="outlined"
            color={getProgramBadgeColor(name)}
            size="sm"
          />
        );
      })}
    </div>
  );
}

/** Profile, assignability, and availability pills for the expanded row. */
function MatchingExpandedLeftColumn({
  data,
  perspective,
  matchingStatusConfig,
  genderConfig,
  assignabilityConfig,
  verifyClicked,
  isVerifying,
  programsList,
  sortedAvailability,
  selectedSlot,
  slotVerifyMap,
  onOpenProfile,
  onVerify,
  onSelectSlot,
}: {
  readonly data: MatchingDashboardItem;
  readonly perspective: MatchingPerspective;
  readonly matchingStatusConfig: (typeof MATCHING_STATUS_CONFIG)[string] | undefined;
  readonly genderConfig: ReturnType<typeof getMatchingGenderConfig>;
  readonly assignabilityConfig: ReturnType<typeof getAssignabilityConfig>;
  readonly verifyClicked: boolean;
  readonly isVerifying: boolean;
  readonly programsList: Program[];
  readonly sortedAvailability: AvailabilitySlot[];
  readonly selectedSlot: AvailabilitySlot | null;
  readonly slotVerifyMap: Map<string, SlotVerifyResult>;
  readonly onOpenProfile: () => void;
  readonly onVerify: () => void;
  readonly onSelectSlot: React.Dispatch<React.SetStateAction<AvailabilitySlot | null>>;
}) {
  const rawSubject = getDashboardRawSubject(data);
  const subject = getRemoteSubjectLabel(rawSubject);
  const validLevels = getDashboardNamedLevels(data);
  const remainingCapacity = getTutorRemainingCapacity(data);
  const showTutorCapacity = perspective === 'tutor' && 'numberOfStudentsToSupport' in data;
  const valorisationBadge = getTutorValorisationBadge(getTutorDashboardValorisation(data));

  function renderSubjectBlock(): React.ReactNode {
    if (!rawSubject) return null;
    return (
      <div>
        <p className="mb-2 text-sm font-medium text-muted-foreground">Matières</p>
        <LabelBadge
          label={subject}
          icon={SUBJECT_DISPLAY_CONFIG.icon}
          variant={SUBJECT_DISPLAY_CONFIG.variant}
          color={getSubjectBadgeColor(subject)}
          size="sm"
        />
      </div>
    );
  }

  function renderValorisationBlock(): React.ReactNode {
    if (perspective !== 'tutor') return null;
    return (
      <div>
        <p className="mb-2 text-sm font-medium text-muted-foreground">Engagement valorisé</p>
        <LabelBadge
          label={valorisationBadge.label}
          icon={valorisationBadge.icon}
          variant="outlined"
          color={valorisationBadge.color}
          size="sm"
        />
      </div>
    );
  }

  function renderSubjectAndValorisationRow(): React.ReactNode {
    const subjectBlock = renderSubjectBlock();
    const valorisationBlock = renderValorisationBlock();
    if (!subjectBlock && !valorisationBlock) return null;
    return (
      <div className="flex flex-wrap items-start gap-8">
        {subjectBlock}
        {valorisationBlock}
      </div>
    );
  }

  return (
    <div
      className="space-y-4 rounded-xl border p-4"
      style={{
        backgroundColor: 'var(--card)',
        borderColor: 'color-mix(in srgb, var(--color-1-nuance-3) 15%, transparent)',
      }}
    >
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <MatchingPerspectiveIcon perspective={perspective} />
            <span className="font-medium text-foreground">{data.fullName}</span>
            <Button preset="row-view" tooltipText="Voir le profil" onClick={onOpenProfile} />
          </div>
          <Button
            size="sm"
            variant="primary"
            className="min-w-[6.5rem] hover:!bg-[var(--color-2-nuance-2)] hover:!border-[var(--color-2-nuance-2)] hover:!text-white [&>*]:hover:!text-white"
            style={{
              backgroundColor: 'var(--color-2-nuance-1)',
              borderColor: 'var(--color-2-nuance-1)',
            }}
            onClick={onVerify}
            disabled={isVerifying}
            loading={isVerifying}
            icon={ScanSearch}
            iconPosition="left"
          >
            {verifyClicked ? 'Revérifier' : 'Vérifier'}
          </Button>
        </div>

        <div className="flex flex-wrap gap-2">
          <LabelBadge
            label={genderConfig.label}
            icon={genderConfig.icon}
            variant={genderConfig.badgeVariant}
            color={genderConfig.badgeColor}
            size="sm"
          />
          {matchingStatusConfig && (
            <LabelBadge
              label={matchingStatusConfig.label}
              icon={matchingStatusConfig.icon}
              variant={matchingStatusConfig.badgeVariant}
              color={matchingStatusConfig.badgeColor}
              size="sm"
            />
          )}
          {verifyClicked && !isVerifying && (
            <LabelBadge
              label={assignabilityConfig.label}
              icon={assignabilityConfig.icon}
              variant={assignabilityConfig.badgeVariant}
              color={assignabilityConfig.badgeColor}
              size="sm"
            />
          )}
        </div>
      </div>

      <div className="space-y-3">
        {renderSubjectAndValorisationRow()}

        {validLevels.length > 0 && (
          <div>
            <p className="mb-2 text-sm font-medium text-muted-foreground">Niveau</p>
            <div className="flex flex-wrap gap-1">
              {validLevels.map((level) => (
                <LabelBadge
                  key={level.levelId}
                  label={level.levelName}
                  icon={LEVEL_BADGE_CONFIG.icon}
                  variant={LEVEL_BADGE_CONFIG.variant}
                  color={getLevelBadgeColor(level.levelName)}
                  size="sm"
                />
              ))}
            </div>
          </div>
        )}

        {data.programs.length > 0 && (
          <div>
            <p className="mb-2 text-sm font-medium text-muted-foreground">Programme</p>
            <div className="flex flex-wrap gap-1">
              {data.programs.map((program) => {
                const displayName = getProgramDisplayName(program, programsList);
                return (
                  <LabelBadge
                    key={program.programId}
                    label={displayName}
                    icon={PROGRAM_DISPLAY_CONFIG.icon}
                    variant={PROGRAM_DISPLAY_CONFIG.variant}
                    color={getProgramBadgeColor(displayName)}
                    size="sm"
                  />
                );
              })}
            </div>
          </div>
        )}

        {showTutorCapacity && (
          <div>
            <p className="mb-2 text-sm font-medium text-muted-foreground">Élèves accompagnés</p>
            <LabelBadge
              label={getMatchingCapacityLabel(data, 'tutor')}
              icon={Users}
              variant="outlined"
              color={getTutorCapacityColor(remainingCapacity)}
              size="sm"
            />
          </div>
        )}
      </div>

      {sortedAvailability.length > 0 && (
        <MatchingAvailabilityGrid
          slots={sortedAvailability}
          selectedSlot={selectedSlot}
          verifyClicked={verifyClicked}
          isVerifying={isVerifying}
          slotVerifyMap={slotVerifyMap}
          onSelectSlot={onSelectSlot}
        />
      )}
    </div>
  );
}

/** Tutor vs student avatar icon in the expanded columns. */
function MatchingPerspectiveIcon({ perspective }: { readonly perspective: MatchingPerspective }) {
  if (perspective === 'tutor') {
    return (
      <div
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
        style={{
          backgroundColor: 'color-mix(in srgb, var(--color-2-nuance-1) 15%, transparent)',
        }}
      >
        <GraduationCap className="h-4 w-4" style={{ color: 'var(--color-2-nuance-1)' }} />
      </div>
    );
  }
  return (
    <div
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
      style={{
        backgroundColor: 'color-mix(in srgb, var(--color-1-nuance-1) 15%, transparent)',
      }}
    >
      <User className="h-4 w-4" style={{ color: 'var(--color-1-nuance-1)' }} />
    </div>
  );
}

/** One availability pill; taken / unassignable stay disabled and show a hover tooltip. */
function MatchingAvailabilitySlotButton({
  slot,
  selectedSlot,
  verifyClicked,
  slotVerifyMap,
  onSelectSlot,
}: {
  readonly slot: AvailabilitySlot;
  readonly selectedSlot: AvailabilitySlot | null;
  readonly verifyClicked: boolean;
  readonly slotVerifyMap: Map<string, SlotVerifyResult>;
  readonly onSelectSlot: React.Dispatch<React.SetStateAction<AvailabilitySlot | null>>;
}) {
  const state = getSlotVisualState(slot, selectedSlot, verifyClicked, slotVerifyMap);
  const isDisabled = state.isTaken || state.isNonAffectable;
  const tooltipText = getSlotDisabledTooltip(state);

  function handleClick(): void {
    if (isDisabled) return;
    onSelectSlot((prev) => (prev?.fullName === slot.fullName ? null : slot));
  }

  const pill = (
    <button
      type="button"
      disabled={isDisabled}
      onClick={handleClick}
      className={getSlotPillClassName(state)}
      style={{
        ...getSlotPillStyle(state),
        fontSize: '0.6rem',
        lineHeight: '1.3',
        fontWeight: 500,
      }}
    >
      <Clock className="h-2.5 w-2.5 shrink-0 opacity-70" />
      <span className="text-center w-full">{formatSlotLabel(slot)}</span>
    </button>
  );

  if (!tooltipText) return pill;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex cursor-not-allowed">{pill}</span>
      </TooltipTrigger>
      <TooltipContent variant="light">
        <p>{tooltipText}</p>
      </TooltipContent>
    </Tooltip>
  );
}

/** Availability pills; click toggles selection, taken / unassignable slots stay disabled. */
function MatchingAvailabilityGrid({
  slots,
  selectedSlot,
  verifyClicked,
  isVerifying,
  slotVerifyMap,
  onSelectSlot,
}: {
  readonly slots: AvailabilitySlot[];
  readonly selectedSlot: AvailabilitySlot | null;
  readonly verifyClicked: boolean;
  readonly isVerifying: boolean;
  readonly slotVerifyMap: Map<string, SlotVerifyResult>;
  readonly onSelectSlot: React.Dispatch<React.SetStateAction<AvailabilitySlot | null>>;
}) {
  function renderSlot(slot: AvailabilitySlot): React.ReactNode {
    return (
      <MatchingAvailabilitySlotButton
        key={slot.fullName}
        slot={slot}
        selectedSlot={selectedSlot}
        verifyClicked={verifyClicked}
        slotVerifyMap={slotVerifyMap}
        onSelectSlot={onSelectSlot}
      />
    );
  }

  return (
    <div>
      <p className="mb-3 text-sm font-medium text-foreground">Disponibilités</p>
      {isVerifying ? (
        <div className="flex items-center justify-center py-8">
          <Spinner size="lg" />
        </div>
      ) : (
        <TooltipProvider delayDuration={200}>
          <div className="flex flex-wrap gap-2">{slots.map(renderSlot)}</div>
        </TooltipProvider>
      )}
    </div>
  );
}

/** Searchable, paginated unmatched-candidates list. */
function MatchingExpandedRightColumn({
  perspective,
  searchQuery,
  onSearch,
  candidatesTotal,
  candidatePage,
  totalCandidatePages,
  onPrevPage,
  onNextPage,
  isLoading,
  visibleCandidates,
  selectedSlot,
  addedCount,
  onEyeClick,
  onPlusClick,
  renderCandidateBadges,
}: {
  readonly perspective: MatchingPerspective;
  readonly searchQuery: string;
  readonly onSearch: (value: string) => void;
  readonly candidatesTotal: number;
  readonly candidatePage: number;
  readonly totalCandidatePages: number;
  readonly onPrevPage: () => void;
  readonly onNextPage: () => void;
  readonly isLoading: boolean;
  readonly visibleCandidates: MatchingCandidate[];
  readonly selectedSlot: AvailabilitySlot | null;
  readonly addedCount: number;
  readonly onEyeClick: (candidateId: string) => void;
  readonly onPlusClick: (candidateId: string) => void;
  readonly renderCandidateBadges: (candidate: MatchingCandidate) => React.ReactNode;
}) {
  const emptyMessage = getEmptyCandidatesMessage(!!selectedSlot, perspective);

  return (
    <div
      className="flex flex-col space-y-4 rounded-xl border p-4"
      style={{
        backgroundColor: 'var(--card)',
        borderColor: 'color-mix(in srgb, var(--color-1-nuance-3) 15%, transparent)',
      }}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <div
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
            style={{
              backgroundColor:
                perspective === 'tutor'
                  ? 'color-mix(in srgb, var(--color-1-nuance-1) 15%, transparent)'
                  : 'color-mix(in srgb, var(--color-2-nuance-1) 15%, transparent)',
            }}
          >
            {perspective === 'tutor' ? (
              <User className="h-4 w-4" style={{ color: 'var(--color-1-nuance-1)' }} />
            ) : (
              <GraduationCap className="h-4 w-4" style={{ color: 'var(--color-2-nuance-1)' }} />
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground">
              {perspective === 'tutor'
                ? getRoleLabelPlural('student')
                : getRoleLabelPlural('tutor')}
            </span>
            <LabelBadge
              label={String(candidatesTotal)}
              variant="ghost"
              color={
                perspective === 'tutor' ? 'var(--color-1-nuance-1)' : 'var(--color-2-nuance-1)'
              }
              size="sm"
            />
          </div>
        </div>

        <div className="flex items-center gap-1">
          {totalCandidatePages > 1 && (
            <span className="text-xs text-muted-foreground mr-1">
              {candidatePage}/{totalCandidatePages}
            </span>
          )}
          <button
            type="button"
            disabled={candidatePage <= 1}
            onClick={onPrevPage}
            className="disabled:opacity-30 hover:opacity-70 transition-opacity"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            disabled={candidatePage >= totalCandidatePages}
            onClick={onNextPage}
            className="disabled:opacity-30 hover:opacity-70 transition-opacity"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <SearchInput
        value={searchQuery}
        onSearch={onSearch}
        placeholder={perspective === 'tutor' ? 'Rechercher un élève' : 'Rechercher un tuteur'}
        debounceMs={300}
        shortcut="Ctrl+/"
      />

      <div className="flex-1 space-y-2 overflow-y-auto">
        <MatchingCandidatesList
          isLoading={isLoading}
          hasCandidates={visibleCandidates.length > 0}
          perspective={perspective}
          emptyMessage={emptyMessage}
          visibleCandidates={visibleCandidates}
          selectedSlot={selectedSlot}
          addedCount={addedCount}
          onEyeClick={onEyeClick}
          onPlusClick={onPlusClick}
          renderCandidateBadges={renderCandidateBadges}
        />
      </div>
    </div>
  );
}

/** Loading / empty / draggable-card list for unmatched candidates. */
function MatchingCandidatesList({
  isLoading,
  hasCandidates,
  perspective,
  emptyMessage,
  visibleCandidates,
  selectedSlot,
  addedCount,
  onEyeClick,
  onPlusClick,
  renderCandidateBadges,
}: {
  readonly isLoading: boolean;
  readonly hasCandidates: boolean;
  readonly perspective: MatchingPerspective;
  readonly emptyMessage: string;
  readonly visibleCandidates: MatchingCandidate[];
  readonly selectedSlot: AvailabilitySlot | null;
  readonly addedCount: number;
  readonly onEyeClick: (candidateId: string) => void;
  readonly onPlusClick: (candidateId: string) => void;
  readonly renderCandidateBadges: (candidate: MatchingCandidate) => React.ReactNode;
}) {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-8">
        <Spinner size="lg" label="Chargement..." />
      </div>
    );
  }

  if (!hasCandidates) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-8">
        {perspective === 'tutor' ? (
          <User className="h-8 w-8 opacity-30" style={{ color: 'var(--color-1-nuance-1)' }} />
        ) : (
          <GraduationCap
            className="h-8 w-8 opacity-30"
            style={{ color: 'var(--color-2-nuance-1)' }}
          />
        )}
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <>
      {visibleCandidates.map((candidate) => {
        const candidateId = getCandidateId(candidate);
        return (
          <DraggableCandidateCard
            key={candidateId}
            candidateId={candidateId}
            candidate={candidate}
            isDisabled={!canAddCandidate(selectedSlot, addedCount)}
            onEyeClick={() => onEyeClick(candidateId)}
            onPlusClick={() => onPlusClick(candidateId)}
            renderBadges={renderCandidateBadges}
          />
        );
      })}
    </>
  );
}

/** Props for {@link DroppableCenterColumn}. */
interface DroppableCenterColumnProps {
  readonly selectedSlot: AvailabilitySlot | null;
  readonly perspective: MatchingPerspective;
  readonly addedCandidates: string[];
  readonly candidatesData: MatchingCandidate[];
  readonly isConfirmDisabled: boolean;
  readonly onRemoveCandidate: (id: string) => void;
  readonly onConfirm: () => void;
  readonly renderCandidateBadges: (candidate: MatchingCandidate) => React.ReactNode;
}

/** Selected slot + dropped candidate + confirm; dashed border while hovering. */
function DroppableCenterColumn({
  selectedSlot,
  perspective,
  addedCandidates,
  candidatesData,
  isConfirmDisabled,
  onRemoveCandidate,
  onConfirm,
  renderCandidateBadges,
}: DroppableCenterColumnProps) {
  const isDropDisabled = !canAddCandidate(selectedSlot, addedCandidates.length);
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: 'candidate-drop-zone',
  });
  const showDropHint = isOver && !isDropDisabled;

  function renderAddedCandidate(candidateId: string): React.ReactNode {
    const candidate = findCandidateById(candidatesData, candidateId);
    if (!candidate) return null;

    function handleRemove(): void {
      onRemoveCandidate(candidateId);
    }

    return (
      <div
        key={candidateId}
        className="flex items-start gap-2 rounded-lg border p-3"
        style={{
          borderColor: 'color-mix(in srgb, var(--status-success) 30%, transparent)',
          backgroundColor: 'color-mix(in srgb, var(--status-success) 8%, transparent)',
        }}
      >
        <div className="flex items-start gap-2 flex-1 min-w-0">
          {'studentUserId' in candidate ? (
            <User
              className="h-4 w-4 shrink-0 mt-0.5"
              style={{ color: 'var(--color-1-nuance-1)' }}
            />
          ) : (
            <GraduationCap
              className="h-4 w-4 shrink-0 mt-0.5"
              style={{ color: 'var(--color-2-nuance-1)' }}
            />
          )}
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">
              {getCandidateDisplayName(candidate)}
            </p>
            {renderCandidateBadges(candidate)}
          </div>
        </div>
        <button
          type="button"
          className="shrink-0 hover:opacity-80"
          style={{ color: 'rgb(var(--color-error))' }}
          onClick={handleRemove}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div
      ref={setDropRef}
      className="flex flex-col space-y-4 rounded-xl border p-4"
      style={{
        backgroundColor: 'var(--card)',
        borderColor: showDropHint
          ? 'var(--color-1-nuance-1)'
          : 'color-mix(in srgb, var(--color-1-nuance-3) 15%, transparent)',
        transition: 'border-color 0.15s',
      }}
    >
      <div className="flex items-center gap-3">
        <div
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
          style={{
            backgroundColor: 'color-mix(in srgb, var(--color-3-nuance-1) 15%, transparent)',
          }}
        >
          <CheckCircle className="h-4 w-4" style={{ color: 'var(--color-3-nuance-1)' }} />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-foreground">Affectation</p>
          {selectedSlot && (
            <div className="mt-1">
              <LabelBadge
                label={selectedSlot.fullName}
                icon={Clock}
                variant="outlined"
                color="var(--color-3-nuance-1)"
                size="sm"
              />
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 space-y-3">
        <p className="text-sm font-medium text-muted-foreground">
          {perspective === 'tutor' ? getRoleLabel('student') : getRoleLabel('tutor')} sélectionné
        </p>

        <div
          className="space-y-2"
          style={{
            minHeight: '100px',
            ...(showDropHint
              ? {
                  border: '2px dashed var(--color-1-nuance-1)',
                  backgroundColor: 'color-mix(in srgb, var(--color-1-nuance-1) 8%, transparent)',
                  borderRadius: '0.5rem',
                  padding: '0.5rem',
                }
              : {}),
          }}
        >
          {addedCandidates.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-8">
              {perspective === 'tutor' ? (
                <User className="h-8 w-8 opacity-30" style={{ color: 'var(--color-1-nuance-1)' }} />
              ) : (
                <GraduationCap
                  className="h-8 w-8 opacity-30"
                  style={{ color: 'var(--color-2-nuance-1)' }}
                />
              )}
              <p className="text-sm text-muted-foreground">
                {getDropZoneEmptyMessage(showDropHint, perspective)}
              </p>
            </div>
          ) : (
            addedCandidates.map(renderAddedCandidate)
          )}
        </div>
      </div>

      <Button
        fullWidth
        variant="primary"
        size="md"
        icon={HeartHandshake}
        iconPosition="left"
        disabled={isConfirmDisabled}
        onClick={onConfirm}
      >
        Confirmer le binôme
      </Button>
    </div>
  );
}

/** Props for {@link DraggableCandidateCard}. */
interface DraggableCandidateCardProps {
  readonly candidateId: string;
  readonly candidate: UnmatchedStudentItem | UnmatchedTutorItem;
  readonly isDisabled: boolean;
  readonly onEyeClick: () => void;
  readonly onPlusClick: () => void;
  readonly renderBadges: (candidate: MatchingCandidate) => React.ReactNode;
}

/** Candidate card; drag is disabled when no slot is selected or the drop zone is full. */
function DraggableCandidateCard({
  candidateId,
  candidate,
  isDisabled,
  onEyeClick,
  onPlusClick,
  renderBadges,
}: DraggableCandidateCardProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: candidateId,
    disabled: isDisabled,
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className="flex items-start justify-between rounded-lg border p-3 cursor-grab active:cursor-grabbing"
      style={{
        borderColor: 'color-mix(in srgb, var(--color-1-nuance-3) 20%, transparent)',
        opacity: getCandidateCardOpacity(isDragging, isDisabled),
        touchAction: 'none',
        userSelect: 'none',
      }}
    >
      <div className="flex items-start gap-2 flex-1 min-w-0">
        {'studentUserId' in candidate ? (
          <User className="h-4 w-4 shrink-0 mt-0.5" style={{ color: 'var(--color-1-nuance-1)' }} />
        ) : (
          <GraduationCap
            className="h-4 w-4 shrink-0 mt-0.5"
            style={{ color: 'var(--color-2-nuance-1)' }}
          />
        )}
        <div className="min-w-0">
          <p className="text-sm text-foreground">{getCandidateDisplayName(candidate)}</p>
          {renderBadges(candidate)}
        </div>
      </div>
      <div className="flex items-center gap-2 ml-2 shrink-0">
        <button
          className="text-[var(--color-1-nuance-1)] hover:text-[var(--color-1-nuance-2)] transition-colors"
          onClick={onEyeClick}
        >
          <Eye className="h-4 w-4" />
        </button>
        <button
          className="text-[var(--color-2-nuance-1)] hover:text-[var(--color-2-nuance-2)] transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          disabled={isDisabled}
          onClick={onPlusClick}
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/** Props for {@link CandidateCardOverlay}. */
interface CandidateCardOverlayProps {
  readonly candidate: UnmatchedStudentItem | UnmatchedTutorItem | null;
  readonly renderBadges: (candidate: MatchingCandidate) => React.ReactNode;
}

/** Pointer-events-none clone shown under the cursor while dragging. */
function CandidateCardOverlay({ candidate, renderBadges }: CandidateCardOverlayProps) {
  if (!candidate) return null;

  return (
    <div
      className="flex items-start justify-between rounded-lg border p-3 shadow-lg rotate-1"
      style={{
        borderColor: 'color-mix(in srgb, var(--color-1-nuance-3) 20%, transparent)',
        backgroundColor: 'var(--card)',
        opacity: 0.85,
        pointerEvents: 'none',
      }}
    >
      <div className="flex items-start gap-2 flex-1 min-w-0">
        {'studentUserId' in candidate ? (
          <User className="h-4 w-4 shrink-0 mt-0.5" style={{ color: 'var(--color-1-nuance-1)' }} />
        ) : (
          <GraduationCap
            className="h-4 w-4 shrink-0 mt-0.5"
            style={{ color: 'var(--color-2-nuance-1)' }}
          />
        )}
        <div className="min-w-0">
          <p className="text-sm text-foreground">{getCandidateDisplayName(candidate)}</p>
          {renderBadges(candidate)}
        </div>
      </div>
      <div className="flex items-center gap-2 ml-2 shrink-0">
        <Eye className="h-4 w-4" style={{ color: 'var(--color-1-nuance-1)' }} />
        <Plus className="h-4 w-4" style={{ color: 'var(--color-2-nuance-1)' }} />
      </div>
    </div>
  );
}
